<script setup lang="ts">
import {resolveMediaUrl, resolveMediaAlt, resolveMediaProps} from '~/utils/relations'
import Tag from '~/components/overflow/tag.vue'
import LikeButton from '~/components/overflow/like-button.vue'
import Copy from '~/components/overflow/copy.vue'

const route = useRoute()

const {
  public: {baseUrl},
} = useRuntimeConfig()

const bytes = useOverflow()
const slug = route.params.slug as string

const {data: rawByte} = await useAsyncData(`byte:${slug}`, () => bytes.fetchBySlug(slug))

if (!rawByte.value) {
  throw createError({
    statusCode: 404,
  })
}

const {data: byte} = useLivePreview({initialData: rawByte.value, depth: 3})
const currentByteUrl = computed(() => `${baseUrl}/technical-bytes/${slug}`)

const seo = byte.value?.meta

useSeoMeta({
  title: byte.value?.title || 'Overflow | Webamboos',
  description: seo?.description,
  ogUrl: `${baseUrl}/technical-bytes/${slug}`,
  ogTitle: seo?.ogTitle || seo?.title || byte.value?.title,
  ogDescription: seo?.ogDescription || seo?.description,
  ogImage: resolveMediaUrl(seo?.image),
  ogImageAlt: resolveMediaAlt(seo?.image),
})
</script>

<template>
  <Container v-if="byte" class="screen-content relative grid grid-cols-4 flex-col px-0 pt-24 sm:pt-32 lg:gap-8 lg:px-0">
    <div class="col-span-full mb-8 flex items-start pl-8 md:pl-12 lg:col-span-1 lg:mt-3 lg:pl-16">
      <BackLink to="/technical-bytes">{{ $t('overflow.back') }}</BackLink>
    </div>

    <header
      id="header"
      class="col-span-4 flex flex-col items-start gap-y-2 px-8 text-left md:px-12 lg:col-span-3 lg:pl-0 lg:pr-16"
    >
      <Heading as="h1" size="large">
        {{ byte.title }}
      </Heading>
      <Paragraph size="large" class="mb-4">
        {{ byte.description }}
      </Paragraph>

      <div class="my-4 flex items-center space-x-4">
        <Tag :tags="byte.tags ?? []" />
      </div>
    </header>

    <aside class="col-span-full flex w-full flex-col rounded-lg px-8 md:px-12 lg:col-span-1 lg:py-8 lg:pl-16 lg:pr-0">
      <div v-if="byte.author" class="flex gap-x-4">
        <NuxtImg
          v-bind="resolveMediaProps(byte.author.image)"
          :width="128"
          :height="128"
          fit="cover"
          class="h-14 w-14 rounded-full object-cover bordershadow-gray"
        />

        <div class="flex flex-col justify-center gap-y-1">
          <Paragraph weight="bold">{{ byte.author?.name }}</Paragraph>
          <Paragraph>{{ byte.author?.role }}</Paragraph>
        </div>
      </div>
    </aside>

    <article class="col-span-4 max-md:px-2 lg:col-span-3">
      <main class="w-full pb-16 max-md:px-6 md:px-12 lg:pl-0 lg:pr-16">
        <RichText v-if="byte.content" :raw="byte.content" />

        <div v-if="byte" class="mt-32 flex gap-x-4">
          <Copy card :byte-id="byte.id" :copy-text="currentByteUrl" :clickable="true" class="cursor-pointer" />

          <LikeButton card :byte="byte" />
        </div>
      </main>
    </article>
  </Container>
</template>
